import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { submitContact } from '../api/services'
import Navbar from '../components/Navbar'
import Footer from '../components/Footer'

// Contact categories offered on the general contact page. Enterprise is
// included here (per product decision) and is the only category that shows and
// requires the company field — mirroring the backend's strict validator.
const CATEGORIES = [
    { value: 'support', label: 'Support' },
    { value: 'billing', label: 'Billing & Payments' },
    { value: 'feedback', label: 'Feedback' },
    { value: 'enterprise', label: 'Enterprise Enquiry' },
    { value: 'other', label: 'Something else' },
]

// Light, category-aware copy for the right-hand form heading. Keeps the tone
// close to the platform's core voice without a bespoke page per category.
const CATEGORY_COPY = {
    support: {
        heading: 'How can we help?',
        sub: "Tell us what you're running into and we'll get you sorted.",
    },
    billing: {
        heading: 'Billing & account questions',
        sub: 'Questions about a charge, plan, or your account — we can help.',
    },
    feedback: {
        heading: "We'd love your thoughts",
        sub: 'Ideas, praise, or gripes — we read every message.',
    },
    enterprise: {
        heading: 'Contact Enterprise Sales',
        sub: 'Let us design a privacy layout tailored to your organization.',
    },
    other: {
        heading: 'Get in touch',
        sub: 'Send us a message and we’ll get back to you.',
    },
}

export default function Contact() {
    const [animateIn, setAnimateIn] = useState(false)
    const [loading, setLoading] = useState(false)
    const [success, setSuccess] = useState(false)
    const [ticketCode, setTicketCode] = useState(null)
    const [error, setError] = useState(null)

    const [formData, setFormData] = useState({
        name: '',
        email: '',
        category: 'support',
        company: '',
        message: '',
    })

    const isEnterprise = formData.category === 'enterprise'

    useEffect(() => {
        const t = setTimeout(() => setAnimateIn(true), 50)
        return () => clearTimeout(t)
    }, [])

    const handleInputChange = (e) => {
        const { name, value } = e.target
        setFormData((prev) => ({ ...prev, [name]: value }))
    }

    const handleSubmit = async (e) => {
        e.preventDefault()
        setLoading(true)
        setError(null)

        // Build the payload. company is sent ONLY for enterprise — the backend
        // strictly rejects a company on any other category, so we omit it.
        const payload = {
            name: formData.name,
            email: formData.email,
            category: formData.category,
            message: formData.message,
        }
        if (formData.category === 'enterprise' && formData.company.trim()) {
            payload.company = formData.company.trim()
        }

        try {
            const res = await submitContact(payload)
            setTicketCode(res.data?.ticket_code || null)
            setSuccess(true)
            setFormData({
                name: '',
                email: '',
                category: formData.category,
                company: '',
                message: '',
            })
        } catch (err) {
            console.error('Contact submission failed:', err)
            const detail = err.response?.data?.detail
            if (Array.isArray(detail)) {
                setError(detail.map((e) => e.msg).join('. '))
            } else if (typeof detail === 'string') {
                setError(detail)
            } else {
                setError(
                    'Unable to submit your message right now. Please check your network connection or try again.'
                )
            }
        } finally {
            setLoading(false)
        }
    }

    const copy = CATEGORY_COPY[formData.category] || CATEGORY_COPY.other

    return (
        <div className="min-h-screen flex flex-col bg-[#07090e]">
            <Navbar />

            {/* Split Premium Frame Layout */}
            <div className="flex flex-col lg:flex-row flex-1">

                {/* Left Side: Structural Context Panel */}
                <div className="lg:w-[40%] bg-[#0B0F17] border-b lg:border-b-0 lg:border-r border-white/[0.04] flex-shrink-0 relative overflow-hidden flex flex-col justify-between p-8 lg:p-16 text-white select-none">
                    <div className="absolute inset-0 pointer-events-none"
                        style={{ background: 'radial-gradient(circle at bottom left, rgba(212,168,67,0.04) 0%, transparent 55%)' }} />

                    <div className="my-auto max-w-sm relative z-10 space-y-6">
                        <div>
                            <p className="text-amber-400 text-[11px] font-sans font-semibold uppercase tracking-widest mb-2.5">
                                We're here to help
                            </p>
                            <h2 className="font-sans font-bold text-3xl lg:text-4xl tracking-tight text-white leading-tight">
                                Talk to the team behind GMC.
                            </h2>
                        </div>

                        <p className="font-sans font-light text-sm leading-relaxed text-slate-400">
                            Whether it's a support question, a billing query, product feedback, or an enterprise enquiry — send us a message and we'll route it to the right person.
                        </p>

                        <div className="space-y-4 border-t border-white/[0.06] pt-6 text-xs font-sans font-light text-slate-400">
                            <div className="flex items-start gap-3">
                                <span className="w-1.5 h-1.5 rounded-full bg-amber-400 mt-1.5 flex-shrink-0 shadow-[0_0_6px_#f59e0b]" />
                                <p>Every message is reviewed by a real person on our team.</p>
                            </div>
                            <div className="flex items-start gap-3">
                                <span className="w-1.5 h-1.5 rounded-full bg-amber-400 mt-1.5 flex-shrink-0 shadow-[0_0_6px_#f59e0b]" />
                                <p>You'll receive a reference number to track your enquiry.</p>
                            </div>
                            <div className="flex items-start gap-3">
                                <span className="w-1.5 h-1.5 rounded-full bg-amber-400 mt-1.5 flex-shrink-0 shadow-[0_0_6px_#f59e0b]" />
                                <p>We aim to respond to all enquiries within <strong className="text-white/80">one business day</strong>.</p>
                            </div>
                            <div className="flex items-start gap-3">
                                <span className="w-1.5 h-1.5 rounded-full bg-amber-400 mt-1.5 flex-shrink-0 shadow-[0_0_6px_#f59e0b]" />
                                <p>
                                    For direct correspondence, reach us at{' '}
                                    <a
                                        href="mailto:admin@guardianmessagecheck.com"
                                        className="font-medium text-amber-400 hover:text-emerald-400 transition-colors duration-200"
                                    >
                                        admin@guardianmessagecheck.com
                                    </a>
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="relative z-10 flex items-center gap-2 text-slate-500 pt-8 lg:pt-0 border-t border-white/[0.04] lg:border-t-0">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                            stroke="#f59e0b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                            <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                        </svg>
                        <span className="text-[10px] font-sans font-medium tracking-wider uppercase text-slate-400">
                            Secure & Confidential
                        </span>
                    </div>
                </div>

                {/* Right Side: Contact Core Sheet Input */}
                <div className="flex-1 flex flex-col justify-center px-6 py-12 lg:p-20 overflow-y-auto bg-[#07090e]">
                    <div className={`w-full max-w-lg mx-auto transition-all duration-700 ease-out transform ${animateIn ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'}`}>

                        {/* Heading Pass — category-aware */}
                        <div className="mb-8">
                            <h1 className="font-sans font-bold text-3xl text-white tracking-tight mb-2">
                                {copy.heading}
                            </h1>
                            <p className="text-sm font-sans font-light text-slate-400">
                                {copy.sub}
                            </p>
                        </div>

                        {success ? (
                            <div className="bg-white/[0.01] border border-white/[0.05] rounded-2xl p-8 text-center shadow-2xl backdrop-blur-md">
                                <div className="w-12 h-12 rounded-full bg-emerald-500/10 flex items-center justify-center mx-auto mb-4 border border-emerald-500/20">
                                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#10B981" strokeWidth="2.5">
                                        <polyline points="20 6 9 17 4 12" />
                                    </svg>
                                </div>
                                <h3 className="text-lg font-sans font-semibold text-white mb-1">Message received</h3>
                                <p className="text-sm font-sans font-light text-slate-400 max-w-sm mx-auto mb-5">
                                    Thank you. Your message has been routed to our team and we'll be in touch.
                                </p>

                                {ticketCode && (
                                    <div className="mb-6 inline-flex flex-col items-center gap-1 px-6 py-4 rounded-xl bg-amber-400/[0.06] border border-amber-400/20">
                                        <span className="text-[10px] font-sans font-semibold tracking-widest uppercase text-slate-400">
                                            Your reference
                                        </span>
                                        <span className="text-xl font-mono font-bold text-amber-400 tracking-wide">
                                            {ticketCode}
                                        </span>
                                    </div>
                                )}

                                <div>
                                    <button
                                        onClick={() => { setSuccess(false); setTicketCode(null) }}
                                        className="px-5 py-2.5 bg-white/[0.04] hover:bg-white/[0.08] border border-white/[0.08] rounded-xl text-xs font-sans font-medium text-white transition-all duration-200"
                                    >
                                        Send Another Message
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <form onSubmit={handleSubmit} className="space-y-5">

                                {error && (
                                    <div className="p-4 bg-red-500/10 border border-red-500/20 text-red-200 rounded-xl text-xs font-sans font-light flex items-center gap-2.5">
                                        <span className="w-2 h-2 rounded-full bg-red-500 flex-shrink-0 animate-pulse" />
                                        {error}
                                    </div>
                                )}

                                {/* Category selector */}
                                <div className="space-y-1.5">
                                    <label htmlFor="category" className="block text-[11px] font-sans font-semibold tracking-wider uppercase text-slate-400">
                                        What's this about?
                                    </label>
                                    <select
                                        id="category"
                                        name="category"
                                        value={formData.category}
                                        onChange={handleInputChange}
                                        className="w-full px-4 py-3 bg-white/[0.02] border border-white/[0.08] rounded-xl text-sm font-sans font-light text-white focus:outline-none focus:border-amber-400/50 focus:ring-1 focus:ring-amber-400/50 transition-all duration-200"
                                    >
                                        {CATEGORIES.map((c) => (
                                            <option key={c.value} value={c.value} className="bg-[#0D1117]">
                                                {c.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    {/* Name */}
                                    <div className="space-y-1.5">
                                        <label htmlFor="name" className="block text-[11px] font-sans font-semibold tracking-wider uppercase text-slate-400">
                                            Your Name
                                        </label>
                                        <input
                                            type="text" id="name" name="name" required
                                            value={formData.name} onChange={handleInputChange}
                                            placeholder="Full Name"
                                            className="w-full px-4 py-3 bg-white/[0.02] border border-white/[0.08] rounded-xl text-sm font-sans font-light text-white placeholder-slate-600 focus:outline-none focus:border-amber-400/50 focus:ring-1 focus:ring-amber-400/50 transition-all duration-200"
                                        />
                                    </div>

                                    {/* Email */}
                                    <div className="space-y-1.5">
                                        <label htmlFor="email" className="block text-[11px] font-sans font-semibold tracking-wider uppercase text-slate-400">
                                            Email
                                        </label>
                                        <input
                                            type="email" id="email" name="email" required
                                            value={formData.email} onChange={handleInputChange}
                                            placeholder="Email Address"
                                            className="w-full px-4 py-3 bg-white/[0.02] border border-white/[0.08] rounded-xl text-sm font-sans font-light text-white placeholder-slate-600 focus:outline-none focus:border-amber-400/50 focus:ring-1 focus:ring-amber-400/50 transition-all duration-200"
                                        />
                                    </div>
                                </div>

                                {/* Company — enterprise only (conditional) */}
                                {isEnterprise && (
                                    <div className="space-y-1.5">
                                        <label htmlFor="company" className="block text-[11px] font-sans font-semibold tracking-wider uppercase text-slate-400">
                                            Company Name
                                        </label>
                                        <input
                                            type="text" id="company" name="company"
                                            required={isEnterprise}
                                            value={formData.company} onChange={handleInputChange}
                                            placeholder="Company Name"
                                            className="w-full px-4 py-3 bg-white/[0.02] border border-white/[0.08] rounded-xl text-sm font-sans font-light text-white placeholder-slate-600 focus:outline-none focus:border-amber-400/50 focus:ring-1 focus:ring-amber-400/50 transition-all duration-200"
                                        />
                                    </div>
                                )}

                                {/* Message */}
                                <div className="space-y-1.5">
                                    <label htmlFor="message" className="block text-[11px] font-sans font-semibold tracking-wider uppercase text-slate-400">
                                        Message
                                    </label>
                                    <textarea
                                        id="message" name="message" required rows={4}
                                        value={formData.message} onChange={handleInputChange}
                                        placeholder="Tell us how we can help..."
                                        className="w-full px-4 py-3 bg-white/[0.02] border border-white/[0.08] rounded-xl text-sm font-sans font-light text-white placeholder-slate-600 focus:outline-none focus:border-amber-400/50 focus:ring-1 focus:ring-amber-400/50 transition-all duration-200 resize-none"
                                    />
                                </div>

                                {/* Privacy note */}
                                <div className="p-3.5 bg-amber-400/[0.02] border border-dashed border-amber-400/10 rounded-xl leading-relaxed text-slate-400 font-sans font-light text-xs">
                                    <span className="font-semibold text-amber-400/90">Privacy note:</span> Please don't include passwords, payment card numbers, or other sensitive credentials in your message.
                                </div>

                                {/* Submit */}
                                <div className="pt-2">
                                    <button
                                        type="submit"
                                        disabled={loading}
                                        className="w-full py-3.5 bg-amber-400 hover:bg-amber-300 text-slate-950 font-sans font-semibold rounded-xl text-sm tracking-wide disabled:bg-slate-800 disabled:text-slate-500 transition-all duration-200 shadow-[0_4px_20px_rgba(212,168,67,0.15)] flex items-center justify-center gap-2"
                                    >
                                        {loading ? (
                                            <>
                                                <svg className="animate-spin h-4 w-4 text-slate-950" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                                                    <circle cx="12" cy="12" r="10" strokeDasharray="32" strokeDashoffset="16" />
                                                </svg>
                                                <span>Sending...</span>
                                            </>
                                        ) : (
                                            <span>Send Message</span>
                                        )}
                                    </button>
                                </div>

                            </form>
                        )}
                    </div>
                </div>

            </div>

            <Footer />
        </div>
    )
}